import { useState, MouseEvent } from 'react';
import {
  Box,
  Popover,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatLockDate, formatLastLogin } from './utils/dateFormatter';

interface LockInfoPopoverProps {
  lockedReason?: string;
  lockedAt?: string;
  lastLoginAt?: string;
}

export const LockInfoPopover = ({
  lockedReason,
  lockedAt,
  lastLoginAt,
}: LockInfoPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{
          ml: 0.5,
          padding: '2px',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16, color: '#c62828' }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: 280,
            maxWidth: 320,
          }
        }}
      >
        <Box p={2}>
          <Typography
            variant="subtitle2"
            fontFamily="GilroySemiBold"
            color="error"
            mb={1.5}
          >
            🔒 Account Locked
          </Typography>

          <Divider sx={{ mb: 1.5 }} />

          {/* Lock Reason */}
          <Box mb={1.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Reason
            </Typography>
            <Typography
              variant="body2"
              fontFamily="GilroyMedium"
              sx={{
                bgcolor: '#ffebee',
                p: 1,
                borderRadius: 1,
                color: '#c62828'
              }}
            >
              {lockedReason || 'No reason provided'}
            </Typography>
          </Box>

          {/* Locked Date */}
          <Box mb={1.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Locked On
            </Typography>
            <Typography variant="body2" fontFamily="GilroyMedium">
              {formatLockDate(lockedAt)}
            </Typography>
          </Box>

          {/* Last Login */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Last Login Before Lock
            </Typography>
            <Typography variant="body2" fontFamily="GilroyMedium">
              {formatLastLogin(lastLoginAt)}
            </Typography>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
